import {
  Directive,
  ElementRef,
  HostBinding,
  Input,
  OnDestroy,
  OnInit,
  Optional,
  Renderer2,
  ViewChild
} from '@angular/core';
import {
  ControlContainer,
  ControlValueAccessor,
  FormControl,
  FormGroupDirective,
  NgControl,
  NgForm
} from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { MenuItem } from 'primeng/api';
import { Nullable } from 'rt-shared-symbols';
import { BehaviorSubject, combineLatest, map, Observable, Subscription } from 'rxjs';
import { filter, take } from 'rxjs/operators';
import {
  ConfirmDialogComponent,
  ConfirmDialogData
} from '../../../../apps/frontend/src/app/confirm-dialog/confirm-dialog.component';
import {
  SharedManageStatusDialogComponent
} from '../../../../apps/frontend/src/app/status-selector/components/status-selector-manage-status-dialog/shared-manage-status-dialog.component';
import { DocStatusDetails, SharedStatusDetailsDialog } from '../../../../apps/frontend/src/app/status-selector/symbols';
import {
  extractCombinedStatusesFromFormControl,
  FormControlCombinedStatuses,
  formControlCombinedStatusesAdapter
} from '../symbols/rt-inputs-control-status-adapter.symbols';
import { RtValidationErrorHandleStrategy } from '../symbols/rt-inputs-types.symbols';
import {
  rtFormsDefineInvalidityObservable,
  rtFormsOverwriteNgControlResetFunction
} from '../symbols/rt-inputs-validity.symbols';

@Directive({
  standalone: true
})
export abstract class RtInputsStatusSelectorDirective implements OnInit, OnDestroy, ControlValueAccessor {
  @ViewChild('textInputElement') public textInputElement?: ElementRef<HTMLInputElement>;

  /** Error handle strategy. */
  @Input() errorHandleStrategy: RtValidationErrorHandleStrategy = RtValidationErrorHandleStrategy.afterLeft;

  /** Relation between the error name imposed by Validators and the error message. */
  @Input() validationMessages: Nullable<{ [key: string]: string }>;

  @Input() required = true;

  @Input() label = '';


  /** Triggered whether the value is changed. Will be overwritten by Angular forms. */
  public onChange?: (value: Nullable<string>) => void;

  /** Triggered whether the control is touched. Will be overwritten by Angular forms. */
  public onTouched?: () => void;

  /** Observable with the combined statuses of the form control. */
  public providedControlStatuses$: Observable<FormControlCombinedStatuses>;

  /** Indicates whether the validation should be displayed. */
  public shouldIndicateInvalidity$: Observable<boolean>;

  /** Form control for the input field. */
  public readonly statusControl = new FormControl<string>(null);

  /** Component subscriptions. Will be unsubscribed on component destroy. */
  public readonly subscription = new Subscription();

  /** Indicates whether the control is submitted. */
  public readonly isControlSubmitted$ = new BehaviorSubject(false);

  /**
   * Describes type of status selector.
   * Viewer - readOnly type; only shows current status and does not provide way to change it.
   * Selector - available options will be provided in the dropdown. First click on it will emit change event.
   * Dialog - available options will be provided in the new modal window. Event will be emitted on the 'save' action.
   */
  @Input() displayType: 'viewer' | 'selector' | 'dialog' = 'viewer';

  /** Contain actual status that should be displayed on the initialization. */
  @Input() selectedStatus: string = null;

  /** List of available statuses. */
  @Input() set availableStatuses(availableStatuses: DocStatusDetails[]) {
    this._availableStatuses = availableStatuses;
    for (const status of availableStatuses) {
      this._availableStatusesById[status.id] = status;
    }
  };

  get availableStatuses(): DocStatusDetails[] {
    return this._availableStatuses;
  }

  get currentStatus(): DocStatusDetails {
    return this._availableStatuses.find(availableStatus => this.selectedStatus === availableStatus.id) || this.defaultStatus;
  }

  private _availableStatuses: DocStatusDetails[];
  private _availableStatusesById: { [key: string]: DocStatusDetails } = {};


  public defaultStatus: DocStatusDetails = {
    id: null,
    hint: null,
    background: 'var(--text-border-light)',
    availableAsOption: false,
    name: 'Не обрано',
    color: '#717171',
    additionalConfirmation: false,
    confirmationText: '',
    detailsText: ''
  };


  @HostBinding('class.disabled') protected isDisabled = false;

  constructor(
    protected renderer: Renderer2,
    public ngControl: NgControl,
    private dialog: MatDialog,
    @Optional() public controlContainer: ControlContainer
  ) {
    // Use the `ngControl` to access the related form control.
    ngControl.valueAccessor = this;

    // Observe the form submit event if the related strategy is set.
    if (!controlContainer) {
      throw new Error(
        `The controlContainer is not found.
           It is impossible to show validation on form submit event if no form is provided.
           The ${RtInputsStatusSelectorDirective.name} should be used within a <form>.
           If this is intended, change the errorHandleStrategy to the different value.`
      );
    }

    // Observe the form submit event to update the submitted state.
    if (controlContainer instanceof NgForm || controlContainer instanceof FormGroupDirective) {
      this.subscription.add(
        controlContainer.ngSubmit.subscribe(() => {
          this.isControlSubmitted$.next(true);
        })
      );
    }

  }

  ngOnInit(): void {
    // Overwrite the reset function of the control to reset the submitted state.
    rtFormsOverwriteNgControlResetFunction(this.ngControl, this.isControlSubmitted$);

    // Define the single event object based on the form control events.
    if (this.ngControl?.control?.events) {
      this.providedControlStatuses$ = this.ngControl.control.events.pipe(
        formControlCombinedStatusesAdapter(extractCombinedStatusesFromFormControl(this.ngControl.control))
      );
    }

    // Determine whether the control should be displayed as invalid.
    this.shouldIndicateInvalidity$ = rtFormsDefineInvalidityObservable(
      this.providedControlStatuses$,
      this.isControlSubmitted$,
      this.errorHandleStrategy
    );

    // Update the control statuses based on the provided statuses.
    this.subscription.add(
      this.providedControlStatuses$.subscribe(status => {
        const currentControlState = extractCombinedStatusesFromFormControl(this.statusControl);
        // If touched status is not synced, update the control status.
        if (status.touched !== currentControlState.touched) {
          if (status.touched) {
            this.statusControl.markAsTouched();
          } else {
            this.statusControl.markAsUntouched();
          }
        }

        // If pristine status is not synced, update the control status.
        if (status.pristine !== currentControlState.pristine) {
          if (status.pristine) {
            this.statusControl.markAsPristine();
          } else {
            this.statusControl.markAsDirty();
          }
        }
      })
    );

    // Notify the value accessor about the touch events in the input field.
    this.subscription.add(
      this.statusControl.events
        .pipe(
          // Cast the event to the Partial<FormControlCombinedStatuses> type as there is no way to cast it to the exact type.
          map(event => event as unknown as Partial<FormControlCombinedStatuses>),

          // Filter the statuses that are touched.
          filter(statuses => !!statuses?.touched)
        )
        .subscribe(() => {
          // Notify the value accessor about the touch events in the input field.
          if (this.onTouched) {
            this.onTouched();
          }
        })
    );

    // Notify the value accessor about the changes in the input field.
    this.subscription.add(
      this.statusControl.valueChanges.subscribe(value => {
        // Notify the value accessor about the changes in the input field.
        if (this.onChange) {
          this.onChange(value);
        }
      })
    );

    this.subscription.add(
      combineLatest([this.shouldIndicateInvalidity$, this.statusControl.valueChanges]).subscribe(([isInvalid]) => {
        if (isInvalid) {
          this.statusControl.setErrors({ invalid: true });
        } else {
          this.statusControl.setErrors(null);
        }
      })
    );
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  /**
   * Required by ControlValueAccessor.
   * Function for overriding the `onChange` class method.
   */
  public registerOnChange(fn: (value: any) => void): void {
    // Override the class method with provided function.
    this.onChange = fn;
  }

  /**
   * Required by ControlValueAccessor.
   * Function for overriding the `onTouched` class method.
   */
  public registerOnTouched(fn: () => void): void {
    // Override the class method with provided function.
    this.onTouched = fn;
  }

  /**
   * Required by ControlValueAccessor.
   * Describes how to update the form model when the value changes.
   */
  public writeValue(value: string): void {
    this.statusControl.setValue(value, { emitEvent: false });
  }

  /**
   * Trigger select status view in the new modal window.
   */
  public openDialog(): void {
    const dialogRef = this.dialog.open<SharedManageStatusDialogComponent, SharedStatusDetailsDialog>(
      SharedManageStatusDialogComponent,
      {
        data: {
          selectedStatus: this.availableStatuses.find(s => s.id === this.statusControl.value)?.name,
          availableStatuses: this.availableStatuses,
          cancel: 'Скасувати',
          confirm: 'Зберегти',
        }
      }
    );

    // Update current status if any were selected.
    dialogRef
      .afterClosed()
      .pipe(
        take(1),
        filter(status => !!status)
      )
      .subscribe(newStatus => {
        if (this.onChange) {
          this.onChange(newStatus);
        }
      });
  }

  public updateCurrentStatus(newStatus: string): void {
    // Find provided status in the list of available statuses.

    const currentStatus = this.availableStatuses.find(availableStatus => newStatus === availableStatus.id);
    if (!currentStatus) {
      throw new Error(
        `Status you are trying to select (${newStatus}) is not presented in list of available statuses: [${this.availableStatuses.map(
          status => status.id
        )}].`
      );
    }


    if (currentStatus.additionalConfirmation) {
      const dialogRef = this.dialog.open<ConfirmDialogComponent, ConfirmDialogData>(
        ConfirmDialogComponent,
        {
          data: {
            caption: currentStatus.confirmationText,
            detailsText: currentStatus.detailsText,
            confirm: 'Продовжити',
            cancel: 'Скасувати'
          }
        }
      );

      // Update current status if any were selected.
      dialogRef
        .afterClosed()
        .pipe(
          take(1),
          filter(confirm => !!confirm)
        )
        .subscribe(() => {
          this.statusControl.setValue(newStatus);
        });
    } else {
      this.statusControl.setValue(newStatus);
    }
  }
}
